<!-- eslint-disable prettier/prettier -->
<template>
  <div class="grid">
    <div class="grid grid-cols-[minmax(80px,125px),minmax(60%,auto)] items-center gap-3 p-5">
      <VcImage
        :src="product?.imgSrc ? product?.imgSrc : product?.images[0]?.url"
        width="125"
        height="125"
        alt=""
        role="presentation"
      />
      <div>
        <div class="mb-1 text-lg font-bold">{{ product?.code }}</div>
        <div v-dompurify-html="description" class="text-base font-medium"></div>
      </div>
    </div>
    <div>
      <TlTabs :tabs="tabs" class="tltabs-sm" @tab-selected="handleTabSelected">
        <template #tab1>
          <div class="tab-content-wrapper documents-tab">
            <div class="grid gap-3">
              <SupportDocuments
                v-if="product"
                :documents="
                  product.assets?.filter(
                    (asset) =>
                      asset.group !== ASSET_GROUP_SDS &&
                      asset.group !== ASSET_GROUP_TSCA &&
                      asset.group !== ASSET_GROUP_DSL,
                  ) as Asset[]
                "
                :product-id="product.id"
                :product-code="product.code"
                display-download-button
              />
              <div
v-if="product?.softwarePageUrlTL"
                class="relative m-5 flex justify-center before:absolute before:left-0 before:top-[45%] before:h-px before:min-w-full before:bg-[var(--color-neutral-a4)] before:content-['']"
              ></div>
              <div v-if="product?.softwarePageUrlTL" class="mx-5 flex justify-between gap-2 text-base">
                <div class="overflow-wrap-anywhere flex items-center gap-2 font-semibold">
                  {{ $t("pages.product.documents.software") }}
                </div>
                <button type="button" class="group font-medium text-[--color-link] hover:text-[--color-neutral-a1]"  @click="navigateToPage">
                  {{ $t("pages.product.documents.software_download_page") }}
                  <VcIcon
                    name="icon-arrow-up-right"
                    :size="14"
                    class="ml-1 mt-[-4px] fill-[--color-link] group-hover:fill-[--color-neutral-a1]"
                  />
                </button>
              </div>
            </div>
          </div>
        </template>
        <template #tab2>
          <div class="tab-content-wrapper warranty-tab">
            <h3 class="!m-0 !text-base font-bold  text-[--color-neutral-a2]">
              {{ $t("pages.product.documents.warranty") }}
            </h3>
            <div class="mb-3 mt-1 text-sm font-medium">
              {{ product?.warrantyTextTL }}
            </div>
          </div>
        </template>
        <template #tab3>
          <div class="tab-content-wrapper compliance-tab">
            <div class="grid gap-4">
             <ComplianceInfo v-if="product" :product="(product as Product)" display-download-button/>
            </div>
          </div>
        </template>
        <template #tab4>
          <div class="tab-content-wrapper sds-tab">
            <div class="grid gap-4">
              <SafetyDataSheets
          v-if="product"
          :assets="(product.assets.filter((asset) => asset.group === ASSET_GROUP_SDS) as Asset[])"
          display-download-button
          :product-id="product.id"
        />
            </div>
          </div>
        </template>
      </TlTabs>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, ref, watchEffect } from "vue";
import { useI18n } from "vue-i18n";
import { useLocalizedUrl } from "@/core/composables/useLocalizedUrl";
import { useProducts } from "@/shared/catalog";
import { ComplianceInfo, SafetyDataSheets, SupportDocuments } from "@/shared/thorlabs/support-documents";
import { ASSET_GROUP_DSL, ASSET_GROUP_SDS, ASSET_GROUP_TSCA } from "@/shared/thorlabs/support-documents/types";
import type { Asset, Product } from "@/core/api/graphql/types";
import TlTabs from "@/shared/thorlabs/shared/components/tl-tabs.vue";

const props = defineProps<IProps>();
const { t } = useI18n();
const { localizeSoftwarePageUrl } = useLocalizedUrl();

export interface IProps {
  currentProduct?: Product | undefined;
  productCode?: string | undefined;
}

const { products, fetchProducts } = useProducts({ withZeroPrice: true });
const product = ref<Product | undefined>(undefined);

const fetchAndAssignProducts = async (ids: string[] | undefined) => {
  if (ids && ids.length > 0) {
    await fetchProducts(
      {
        filter: `sku:${ids.join(",")}`,
      },
      false,
    );
  }
  product.value = products.value[0];
};

watchEffect(async () => {
  if (props.currentProduct && !props.productCode) {
    product.value = props.currentProduct;
  }
  if (!props.currentProduct && props.productCode) {
    await fetchAndAssignProducts([props.productCode]);
  }
});

interface ITab {
  contentLink: {
    expanded: {
      tabId: string;
      name: string;
      content: string;
    };
  };
}
const isCalibrated = computed(() => (product.value?.isCalibrationServiceTL ? true : false));
const softwarePageUrl = computed(() =>
  product.value?.softwarePageUrlTL ? localizeSoftwarePageUrl(product.value.softwarePageUrlTL) : undefined,
);

// Tabs data
const tabs = computed<ITab[]>(() => {
  const result = [
    { contentLink: { expanded: { tabId: "tab1", name: `${t("pages.product.documents.documents")}`, content: "" } } },
    { contentLink: { expanded: { tabId: "tab2", name: `${t("pages.product.documents.warranty")}`, content: "" } } },
  ];

  if (!isCalibrated.value) {
    result.push({
      contentLink: {
        expanded: { tabId: "tab3", name: `${t("pages.product.documents.compliance")}`, content: "" },
      },
    });
  }
  if (product.value && product?.value?.assets?.some((asset) => asset.group === ASSET_GROUP_SDS)) {
    result.push({
      contentLink: { expanded: { tabId: "tab4", name: `${t("pages.product.documents.sds")}`, content: "" } },
    });
  }

  return result;
});

const activeTab = ref(tabs.value[0]);

const description = computed(() => {
  if (product.value?.fallbackName) {
    return product.value?.fallbackName as string | undefined;
  }

  return product.value?.descriptions
    ? product.value?.descriptionsWithFallbacksTL.find((d) => d.reviewType === "Title")?.content || ""
    : getVariationDescription(product.value?.name, product.value?.code);
});

function handleTabSelected(tab: ITab) {
  activeTab.value = tab; // Set the full tab object as activeTab
}

function navigateToPage() {
  window.location.href = softwarePageUrl.value || "#";
}

function getVariationDescription(variationDescription: string | undefined, variationCode: string | undefined) {
  if (!variationDescription || !variationCode) {
    return "";
  }

  if (variationDescription === variationCode) {
    return "";
  }

  return variationDescription;
}
</script>

<style lang="scss" scoped>
.tltabs-sm {
  :deep() {
    .tl-tabs {
      @apply p-0 gap-3 md:gap-[38px] mx-5 z-[2] relative;

      button {
        @apply py-2 px-0 #{!important};
        @apply font-medium border-b-[3px] flex-none text-base;
      }
      .default-tab {
        @apply border-transparent hover:border-b-[--color-primary-a1];
      }
      .active-tab {
        @apply border-b-[--color-primary-a1] font-bold text-[--color-primary-a1];
      }
    }
  }
  .tab-content-wrapper {
    @apply mt-[-1px] relative z-[1] border-t border-t-[--color-neutral-a4] p-5 pb-0;
  }
}
</style>
